(function ($) {
    /**
     * jQuery Plugin: expandableColumns
     *
     * A plugin for truncating text in specific table columns while preserving clickable elements (like links),
     * with the ability to toggle between truncated and full text on button click.
     * Useful for displaying long identifiers (like IDs) in a compact form.
     * #### Example of usage
     * When you use expandableColumns, you need to have table and some selector for that table (class or id)
     * ```html
     * <table class="table table-hover table-striped table-sm" id="expandable-table">
     *   <thead>
     *     <tr>
     *       <th width="100">ID</th>
     *       <th>Title</th>
     *       <th class="text-end" width="35"></th>
     *     </tr>
     *   </thead>
     *   <tbody>
     *     <tr> // Optionally Put class "skip-expandable" on tr that you want to skip
     *       <td>
     *         <a href="#" class="stretched-link">12345678901234567890</a>
     *       </td>
     *       <td>
     *         Long title of the item
     *       </td>
     *       <td class="text-end">
     *         <button class="btn btn-danger btn-sm">Delete</button>
     *       </td>
     *     </tr>
     *   </tbody>
     * </table>
     * ```
     * ```javascript
     * $('#expandable-table').expandableColumns({
     *     columnsToExpand: [0], // Only apply to the first column (index 0)
     *     firstLetters: 3,      // Show first 3 characters
     *     lastLetters: 4,       // Show last 4 characters
     *     separator: "....",    // Use custom separator
     *     buttonClass: 'btn btn-outline-secondary btn-sm', // Custom button styling
     *     expandIconClass: 'bi-plus-lg',
     *     collapseIconClass: 'bi-dash-lg',
     *     skipRowClass: 'skip-expandable', // Put this class on tr that you want to skip
     *   });
     * ```
     *
     * @param {Object} options - Configuration options for the plugin
     * @param {number} [options.firstLetters=3] - Number of characters to keep at the beginning of the truncated text
     * @param {number} [options.lastLetters=4] - Number of characters to keep at the end of the truncated text
     * @param {string} [options.separator="..."] - Separator used between beginning and ending parts of the text
     * @param {number[]} [options.columnsToExpand=[]] - Array of column indices (starting from 0) that should be expandable
     * @param {string} [options.expandIconClass="bi-arrows-expand-vertical"] - Bootstrap icon class for expand state
     * @param {string} [options.collapseIconClass="bi-arrows-collapse-vertical"] - Bootstrap icon class for collapse state
     * @param {string} [options.buttonClass="btn btn-secondary btn-sm"] - CSS class applied to toggle button
     *
     * @returns {jQuery} The jQuery object for chaining
     */
    $.fn.expandableColumns = function (options) {
        const settings = $.extend({
            firstLetters: 2,
            lastLetters: 4,
            separator: "...",
            columnsToExpand: [],
            expandIconClass: 'bi-arrows-expand-vertical',
            collapseIconClass: 'bi-arrows-collapse-vertical',
            buttonClass: 'btn btn-secondary btn-sm',
            skipRowClass: 'skip-expandable',
        }, options);

        /**
         * Truncates the provided text based on configured first/last character counts.
         *
         * @param {string} text - The full text to truncate
         * @returns {string} The truncated text with a separator
         */
        const truncateText = (text) => {
            if (typeof text !== 'string') return text;
            if (text.length <= settings.firstLetters + settings.lastLetters) return text;

            const start = text.substring(0, settings.firstLetters);
            const end = text.substring(text.length - settings.lastLetters);
            return `${start}${settings.separator}${end}`;
        };

        /**
         * Detects an "empty state" row: a single cell spanning multiple columns.
         * (e.g., <tr><td colspan="6" class="text-center">Table is empty</td></tr>)
         *
         * @param {jQuery} $row
         * @returns {boolean}
         */
        const isEmptyStateRow = ($row) => {
            const $cells = $row.children('td, th');
            if ($cells.length !== 1) return false;
            return $cells.eq(0).is('[colspan]');
        };

        /**
         * Extracts readable text from a <td> based on its child structure.
         * Ignores cells containing .btn elements.
         *
         * @param {jQuery} $cell - The table cell to extract from
         * @returns {string|null} Text to truncate or null if unsupported
         */
        const extractCellText = ($cell) => {
            if ($cell.find('.btn').length > 0) return null;

            const children = $cell.children();

            if (children.length === 0) {
                return $cell.text().trim();
            }

            if (children.length === 1 && children.is('a:not(.btn)')) {
                return children.text().trim();
            }

            if (children.length > 0 && children.filter(':not(span):not(p):not(strong)').length === 0) {
                return $cell.text().trim();
            }

            return null;
        };

        /**
         * Sets the text in a table cell while preserving its original inner structure (e.g., <a>, <span>).
         *
         * @param {jQuery} $cell - The table cell to modify
         * @param {string} newText - The text to insert
         */
        const setTextPreservingStructure = ($cell, newText) => {
            const $link = $cell.find('a:not(.btn)');
            if ($link.length === 1) {
                $link.text(newText);
                return;
            }

            const $wrapped = $cell.children('span, p, strong');
            if ($wrapped.length === 1) {
                $wrapped.text(newText);
                return;
            }

            if ($cell.children().length === 0) {
                $cell.text(newText);
            }
        };

        /**
         * Toggles the column between expanded (full text) and collapsed (truncated text) states.
         *
         * @param {jQuery} $th - The table header element containing the toggle button
         * @param {boolean} isExpanded - Whether the column is currently expanded
         */
        const toggleColumn = ($th, isExpanded) => {
            const colIndex = $th.index();
            const $table = $th.closest('table');
            const $cells = $table.find(`td:nth-child(${colIndex + 1})`);

            $cells.each(function () {
                const $cell = $(this);
                const $row = $cell.closest('tr');
                // Skip empty-state rows
                if (isEmptyStateRow($row)) return;

                const fullText = $cell.data('full-text');

                if (fullText !== undefined) {
                    const newText = isExpanded ? truncateText(fullText) : fullText;
                    setTextPreservingStructure($cell, newText);
                }
            });

            const $icon = $th.find('.expand-btn i');
            $icon
                .removeClass(isExpanded ? settings.collapseIconClass : settings.expandIconClass)
                .addClass(isExpanded ? settings.expandIconClass : settings.collapseIconClass);

            $th.find('.expand-btn').data('expanded', !isExpanded);
        };

        /**
         * Initializes a single column by appending the toggle button and applying truncation.
         *
         * @param {jQuery} $table - The table containing the column
         * @param {number} index - The column index to initialize
         */
        const initColumn = ($table, index) => {
            const $th = $table.find('th').eq(index);
            const thWidthAttr = $th.attr('width');
            const cellWidth = thWidthAttr ? thWidthAttr + 'px' : 'auto';
            const $cells = $table.find(`td:nth-child(${index + 1})`);
            let shouldRenderButton = false;

            $cells.each(function () {
                const $cell = $(this);
                const $row = $cell.closest('tr');
                if ($row.hasClass(settings.skipRowClass)) return;
                // Skip empty-state rows
                if (isEmptyStateRow($row)) return;

                const text = extractCellText($cell);
                if (text !== null && text.length > settings.firstLetters + settings.lastLetters) {
                    shouldRenderButton = true;
                    return false;
                }
            });

            if (shouldRenderButton){
                const $btn = $('<button>', {
                    type: 'button',
                    class: `expand-btn ${settings.buttonClass}`,
                    html: `<i class="bi ${settings.expandIconClass}"></i>`,
                }).data('expanded', false);

                $btn.on('click', function (e) {
                    e.stopPropagation();
                    const isExpanded = $(this).data('expanded');
                    toggleColumn($th, isExpanded);
                });

                $th.prepend($btn);
                $cells.each(function () {
                    const $cell = $(this);
                    const $row = $cell.closest('tr');

                    if ($row.hasClass(settings.skipRowClass)) return;
                    // Skip empty-state rows
                    if (isEmptyStateRow($row)) return;

                    const text = extractCellText($cell);
                    if (text !== null) {
                        $cell
                            .data('full-text', text)
                            .css({ width: cellWidth, maxWidth: "max-content" });

                        setTextPreservingStructure($cell, truncateText(text));
                    }
                });
            }
        };

        /**
         * Main initializer for the plugin.
         * Iterates through each table and sets up the specified columns.
         *
         * @returns {jQuery}
         */
        return this.each(function () {
            const $table = $(this);
            const colIndexes = settings.columnsToExpand.length > 0
                ? settings.columnsToExpand
                : $table.find('th').map((_, el) => $(el).index()).get();

            colIndexes.forEach(index => initColumn($table, index));
        });
    };
})(jQuery);
